import { useMemo, useState } from "react";
import { EstimateType, Rule, RuleRunCriteria, RuleTemplate, RuleTemplateInputField } from "@rebuild/shared";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { validateRuleInputs } from "../utils/validateRuleInputs";
import { toast } from "react-toastify";
import { InputFieldComponent } from "./components/InputFields/InputFieldComponent";
import { RuleAuditTrail } from "./components/RuleAuditTrail";

interface CreateRulePopupProps {
  ruleTemplates: RuleTemplate[];
  onClose: () => void;
  onRuleSave: (rule: Rule, note: string) => void;
  rule?: Rule;
}

interface RuleNameFormProps {
  ruleName: string;
  onUpdate: (name: string) => void;
  isRequiredButMissing?: boolean;
}

const RULE_RUN_CRITERIA_INPUT_FIELD: RuleTemplateInputField = {
  name: "runCriteria",
  type: "object",
  properties: [
    {
      name: "organizations",
      type: "organizations",
    },
    {
      name: "catNumbers",
      type: "catCodes",
    },
    {
      name: "estimateType",
      type: "enum",
      values: [...Object.values(EstimateType)],
    },
    {
      name: "tpas",
      type: "tpas",
    },
    {
      name: "carriers",
      type: "carriers",
    },
    {
      name: "isMitToRecon",
      type: "boolean",
    },
  ],
};

export const CreateRulePopup: React.FC<CreateRulePopupProps> = ({ ruleTemplates, onClose, onRuleSave, rule }) => {
  const [selectedRuleTemplate, setSelectedRuleTemplate] = useState<RuleTemplate | undefined>(
    ruleTemplates.find((ruleTemplate) => ruleTemplate.id === rule?.templateId),
  );
  const [ruleInputs, setRuleInputs] = useState<{ ruleInputs: any }>({ ruleInputs: rule?.ruleInputs ?? {} });
  const [runCriteria, setRunCriteria] = useState<{ runCriteria: RuleRunCriteria }>({ runCriteria: rule?.runCriteria ?? {} });
  const [ruleName, setRuleName] = useState<string>(rule?.name ?? "");
  const [ruleUuid, setRuleUuid] = useState(rule?.uuid ?? new Date().getTime().toString());
  const [isDisabled, setIsDisabled] = useState<boolean>(!!rule?.isDisabled);
  const [isDev, setIsDev] = useState<boolean>(!!rule?.isDev);
  // A fresh explanation is required for every save; we never prefill it from prior events.
  const [note, setNote] = useState("");

  const [showMissingFieldMessages, setShowMissingFieldsMessages] = useState(false);
  // automatically show validation errors if we're opening up an existing rule
  const [showValidationError, setShowValidationError] = useState(!!rule?.uuid);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);

  const { dbLineItems } = useLineItemTemplates();

  const newRule = useMemo(
    () =>
      selectedRuleTemplate?.id
        ? {
            isDev,
            isDisabled,
            name: ruleName,
            ruleInputs: ruleInputs.ruleInputs,
            templateId: selectedRuleTemplate.id,
            uuid: ruleUuid,
            runCriteria: runCriteria.runCriteria,
          }
        : undefined,
    [isDev, isDisabled, ruleName, ruleInputs, selectedRuleTemplate, ruleUuid, runCriteria],
  );

  // we want to continuously update the validatino error but only show it if the user clicks "save"; this way we
  // automatically hide it when the user fixes whatever validation error is there
  const validationErrors = useMemo(() => {
    if (newRule !== undefined) {
      return validateRuleInputs(newRule, dbLineItems);
    }
  }, [validateRuleInputs, newRule, dbLineItems]);

  const validationErrorCount = useMemo(() => validationErrors?.size ?? 0, [validationErrors]);

  const isRuleNameMissing = useMemo(() => !ruleName, [ruleName]);
  const isNoteMissing = useMemo(() => !note.trim(), [note]);
  const isSaveDisabled = (!!showValidationError && !!validationErrorCount) || isNoteMissing;

  // Drafts are works in progress, so we let them save with validation errors; promoting a draft goes through the
  // same validation as any non-draft save.
  const onSave = (isDraft: boolean) => {
    if (newRule === undefined) {
      return;
    }

    if (isRuleNameMissing || isNoteMissing) {
      setShowMissingFieldsMessages(true);
      return;
    }

    if (selectedRuleTemplate === undefined) {
      toast.error(`No rule template selected, can't create rule`);
      return;
    }

    if (!isDraft && validationErrors && validationErrors.size > 0) {
      setShowValidationError(true);
      return;
    }

    onRuleSave({ ...newRule, isDraft }, note.trim());
  };

  const onArchiveConfirm = () => {
    if (rule === undefined) {
      return;
    }
    // Archiving is an update and still requires a note; fall back to a default when the user hasn't typed one.
    onRuleSave({ ...rule, archived: true }, note.trim() || "Archived rule");
    setShowArchiveConfirmation(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {showArchiveConfirmation && (
        <ArchiveRuleConfirmation ruleName={ruleName} onCancel={() => setShowArchiveConfirmation(false)} onConfirm={onArchiveConfirm} />
      )}
      <div className="bg-white rounded-lg shadow-lg w-11/12 h-5/6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl">
          ×
        </button>
        <div className="p-6 w-full h-full flex flex-col">
          <div className="mb-4">
            {rule?.isDraft && (
              <span className="inline-block mb-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold">Draft</span>
            )}
            {/* only allow template selection if the pop-up is for a new rule */}
            {rule === undefined && (
              <div className="flex overflow-x-auto space-x-4 pb-2">
                {ruleTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedRuleTemplate(template)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedRuleTemplate?.id === template.id ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedRuleTemplate && (
            <div className="flex-1 min-h-0 flex flex-row gap-6">
              <div className="flex flex-col flex-1 min-h-0 min-w-0 space-y-8 pr-2">
                <RuleNameForm
                  ruleName={ruleName}
                  onUpdate={setRuleName}
                  isRequiredButMissing={showMissingFieldMessages && isRuleNameMissing}
                />
                <div className="bg-gray-50 p-4 rounded-lg shrink-0">
                  <div className="flex flex-row gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isDisabled}
                        onChange={(e) => setIsDisabled(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 font-medium">Is Disabled</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isDev}
                        onChange={(e) => setIsDev(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 font-medium">Is Dev Rule</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col flex-1 min-h-0 space-y-8 overflow-y-auto pr-2">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Run Criteria</h3>
                    <div className="space-y-6">
                      <InputFieldComponent
                        objectInput={runCriteria}
                        key={`${selectedRuleTemplate.inputField.name}_run_criteria`}
                        inputField={RULE_RUN_CRITERIA_INPUT_FIELD}
                        inputValue={runCriteria}
                        path={RULE_RUN_CRITERIA_INPUT_FIELD.name}
                        onUpdate={(updates) => {
                          setRunCriteria((prev) => ({
                            ...prev,
                            ...updates,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-4">Input Parameters</h3>
                    <div className="space-y-6">
                      <InputFieldComponent
                        objectInput={ruleInputs}
                        key={`${selectedRuleTemplate.inputField.name}_input_parameters`}
                        inputField={selectedRuleTemplate.inputField}
                        inputValue={ruleInputs}
                        path={selectedRuleTemplate.inputField.name}
                        validationErrors={showValidationError ? validationErrors : undefined}
                        onUpdate={(updates) => {
                          setRuleInputs((prev) => ({
                            ...prev,
                            ...updates,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-4">
                      Reason for Change <span className="text-red-500">*</span>
                    </h3>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      placeholder="Explain why you're creating or updating this rule..."
                      className={`w-[95%] mx-2 resize-y rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                        showMissingFieldMessages && isNoteMissing
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                    {showMissingFieldMessages && isNoteMissing && <p className="text-red-500 text-sm mt-1">A note is required.</p>}
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    {showValidationError && !!validationErrorCount && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{`Please resolve the ${validationErrorCount} validation errors before saving.`}</div>
                    )}
                    <div className="flex gap-3">
                      {(rule === undefined || rule.isDraft) && (
                        <button
                          disabled={isNoteMissing}
                          onClick={() => onSave(true)}
                          className={`flex-1 border ${isNoteMissing ? "border-gray-200 text-gray-400 bg-white" : "border-blue-500 text-blue-500 bg-white hover:bg-blue-50"} font-medium py-3 px-4 rounded-lg transition-colors`}
                        >
                          {rule === undefined ? "Create as Draft" : "Update and Keep as Draft"}
                        </button>
                      )}
                      <button
                        disabled={isSaveDisabled}
                        onClick={() => onSave(false)}
                        className={`flex-1 ${isSaveDisabled ? "bg-gray-200" : "bg-blue-500 hover:bg-blue-600"} text-white font-medium py-3 px-4 rounded-lg transition-colors`}
                      >
                        {rule === undefined ? "Create Rule" : rule.isDraft ? "Update and Promote Rule" : "Save Rule"}
                      </button>
                      {rule !== undefined && (
                        <button
                          onClick={() => setShowArchiveConfirmation(true)}
                          className="bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                        >
                          {rule.isDraft ? "Archive" : "Archive Rule"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-1/3 min-w-0 shrink-0 border-l border-gray-200 pl-4 overflow-y-auto">
                <RuleAuditTrail auditTrail={rule?.auditTrail} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ArchiveRuleConfirmationProps {
  ruleName: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const ArchiveRuleConfirmation: React.FC<ArchiveRuleConfirmationProps> = ({ ruleName, onCancel, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Archive Rule</h3>
        <p className="text-gray-600 mb-6">
          {`Are you sure you want to archive "${ruleName || "this rule"}"? Archived rules are hidden from the rules page and will no longer run during rules engine processing. The rule will be preserved in the database and can be restored later if needed.`}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
          >
            Archive
          </button>
        </div>
      </div>
    </div>
  );
};

const RuleNameForm: React.FC<RuleNameFormProps> = ({ ruleName, onUpdate, isRequiredButMissing }) => {
  return (
    <div className="shrink-0">
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex flex-row w-full items-center gap-4">
          <div className="w-48 text-gray-700 text-lg font-bold">Rule Name</div>
          <div className="w-full px-2">
            <input
              type="text"
              value={ruleName}
              onChange={(e) => onUpdate(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                isRequiredButMissing ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="Enter rule name..."
            />
            {isRequiredButMissing && <p className="text-red-500 text-sm mt-1">Rule name is required</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
